import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useConsolePaceUnit } from "../../hooks/useConsolePaceUnit";
import { useBlobUrlMapForFileSlots } from "../../hooks/useBlobUrlMapForFileSlots";
import {
  CLUB_EDITABLE_LINK_FIELDS,
  isGreaterLosAngelesCity,
  isSouthFloridaCity,
  getConsoleRegionOptions,
  getClubRegionForFilter,
  getApiErrorMessage,
  getCityFromSearchParams,
  getConsoleCityTabPath,
  getConsoleMeetingsRangeFromSearchParams,
  getConsoleClubUrl,
  getConsoleRunUrl,
  clubCompleteness,
  getClubAuxiliaryLinkFieldKeys,
  getClubDetailStats,
  getConsoleRangeLabel,
  formatConsoleScheduleWindowSubtitleUtc,
  getConsoleScheduleWindowRunsEmptyMessage,
  isRunUpcomingInScheduleWindow,
  isRunCompletedInScheduleWindow,
  isClubEditableLinkField,
  CLAIM_LABEL,
  MAX_CAROUSEL_IMAGES,
  buildImagesArrayFromUrls,
  dedupeImageUrlsPreserveOrder,
  normalizeExistingImages,
  normalizeImageUrlForDedupe,
  safeExternalHref,
  collectUrlsFromCarouselSlots,
} from "../../utils/consoleUtils";
import {
  INITIAL_ONBOARD_FORM,
  INTERNAL_NOTES_MAX_LENGTH,
  buildOnboardPayload,
  validateConsoleOnboardPrerequisites,
} from "../../utils/consoleOnboardPayload";
import { notifyIfOnboardCredentialsSheetFailed } from "../../utils/onboardCredentialsSheetToast";
import { ConsoleImagePreviewDialog } from "./ConsoleClubFormShared";
import ClubsOnboardClubModal from "./ClubsOnboardClubModal";
import ClubsEditClubModal from "./ClubsEditClubModal";
import {
  CONSOLE_RUN_TIMEZONE_UNSET_NOTE_LIST,
  formatDateTimeInTimezone,
  getConsoleRunDisplayTimezone,
  isConsoleRunTimezoneUnset,
} from "../../utils/dateTimeUtils";
import { ArrowLeft } from "lucide-react";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader } from "./ConsoleShared";
import ClubDetail from "./ClubDetail";
import "../../styles/console.css";
import { buildEditPayload, INITIAL_EDIT_FORM, isMeetingVisible, paceLabelsToCodes } from "../../utils/clubsViewHelpers";
import {
  serializeAddClubPasteUrlsForDirty,
  serializeClubEditCarouselSlotsForDirty,
  serializeFileArrayForDirty,
} from "../../utils/consoleUnsavedFormGuards";

export default function ClubsView({ onQualityMetricsChange, recurringQualityUtcRange = null } = {}) {
  const { unit: consolePaceUnit } = useConsolePaceUnit();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const meetingsRange = getConsoleMeetingsRangeFromSearchParams(searchParams);
  const pathMatch = location.pathname.match(/^\/console\/clubs\/([^/]+)$/);
  const clubIdFromPath = pathMatch ? pathMatch[1] : null;

  const [search, setSearch] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterClaim, setFilterClaim] = useState("");
  const [filterCompleteness, setFilterCompleteness] = useState("");

  const [showAddClubModal, setShowAddClubModal] = useState(false);
  const [unsavedClubModalKind, setUnsavedClubModalKind] = useState(null);
  const addClubOpenBaselineRef = useRef(null);
  const editClubOpenBaselineRef = useRef(null);
  const [addClubForm, setAddClubForm] = useState(INITIAL_ONBOARD_FORM);
  const [addClubSubmitting, setAddClubSubmitting] = useState(false);
  const [showOnboardPassword, setShowOnboardPassword] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [carouselImageFiles, setCarouselImageFiles] = useState([]);
  const [addClubHeroPasteUrl, setAddClubHeroPasteUrl] = useState("");
  const [addClubProfilePasteUrl, setAddClubProfilePasteUrl] = useState("");
  const [addClubCarouselPasteUrls, setAddClubCarouselPasteUrls] = useState([]);

  /** Shared image preview URLs (single or multiple for carousel). Empty = closed. Blob URLs revoked on close. */
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const closeImagePreview = useCallback(() => {
    setImagePreviewUrls((prev) => {
      prev.forEach((u) => {
        if (typeof u === "string" && u.startsWith("blob:")) URL.revokeObjectURL(u);
      });
      return [];
    });
  }, []);

  // Edit club modal (same fields as onboard minus organizer email/password)
  const [editModalClub, setEditModalClub] = useState(null);
  const [editForm, setEditForm] = useState({ ...INITIAL_EDIT_FORM });
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  /** 'hero' | 'profile' | 'carousel' — inline paste URL save in edit club modal */
  const [editClubPasteFieldSaving, setEditClubPasteFieldSaving] = useState(null);
  const [editHeroFile, setEditHeroFile] = useState(null);
  const [editProfileFile, setEditProfileFile] = useState(null);
  const [editHeroPasteUrl, setEditHeroPasteUrl] = useState("");
  const [editProfilePasteUrl, setEditProfilePasteUrl] = useState("");
  /** Edit club: ordered carousel slots (remote URLs already on club or new files to upload). */
  const [editCarouselSlots, setEditCarouselSlots] = useState([]);
  const editCarouselBlobById = useBlobUrlMapForFileSlots(editCarouselSlots);
  /** Full club data loaded when opening edit (for merging existing clubcard_images). */
  const [editFullClub, setEditFullClub] = useState(null);
  /** Edit club: scraped image URL currently being removed (trimmed), for button busy state. */
  const [editScrapedImageRemovingUrl, setEditScrapedImageRemovingUrl] = useState(null);

  const onboardCarouselPasteCapacity = useMemo(() => {
    const hasOnboardHero =
      Boolean(heroImageFile) || (addClubHeroPasteUrl ?? "").trim().startsWith("http");
    return Math.max(0, MAX_CAROUSEL_IMAGES - (hasOnboardHero ? 1 : 0) - carouselImageFiles.length);
  }, [heroImageFile, addClubHeroPasteUrl, carouselImageFiles.length]);

  // Upcoming runs modal (schedule window from context bar)
  const [upcomingRunsModalClub, setUpcomingRunsModalClub] = useState(null);
  const [upcomingRunsList, setUpcomingRunsList] = useState([]);
  const [upcomingRunsLoading, setUpcomingRunsLoading] = useState(false);

  // Runs list modal (upcoming or completed for a club, from loaded runs data)
  const [runsListModal, setRunsListModal] = useState(null);

  // Members modal (club members list for club detail)
  const [membersModalClub, setMembersModalClub] = useState(null);
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(false);
  const [membersTotal, setMembersTotal] = useState(0);

  // Club detail: preview a carousel image in the hero (view only, does not change order)
  const [detailCarouselPreviewUrl, setDetailCarouselPreviewUrl] = useState(null);
  const [clubDeleteBusy, setClubDeleteBusy] = useState(false);

  // Internal notes modal (quick-edit from list or detail)
  const [internalNotesModalClub, setInternalNotesModalClub] = useState(null);
  const [internalNotesValue, setInternalNotesValue] = useState("");
  const [internalNotesSaving, setInternalNotesSaving] = useState(false);
  const [claimMetrics, setClaimMetrics] = useState(null);
  const [claimMetricsLoading, setClaimMetricsLoading] = useState(false);
  const claimMetricsSeqRef = useRef(0);

  useEffect(() => {
    if (internalNotesModalClub) {
      setInternalNotesValue(internalNotesModalClub.internal_notes ?? "");
    }
  }, [internalNotesModalClub]);

  const loadClaimMetrics = useCallback(async () => {
    if (!cityId) {
      claimMetricsSeqRef.current += 1;
      setClaimMetrics(null);
      setClaimMetricsLoading(false);
      return;
    }
    const seq = (claimMetricsSeqRef.current += 1);
    setClaimMetricsLoading(true);
    try {
      const d = await consoleApi.clubs.getClaimMetrics(cityId);
      if (seq !== claimMetricsSeqRef.current) return;
      setClaimMetrics(d || null);
    } catch {
      if (seq !== claimMetricsSeqRef.current) return;
      setClaimMetrics(null);
    } finally {
      if (seq === claimMetricsSeqRef.current) {
        setClaimMetricsLoading(false);
      }
    }
  }, [cityId]);

  useEffect(() => {
    loadClaimMetrics();
  }, [loadClaimMetrics]);

  const { data: clubsRaw, loading, error, refetch: refetchClubs } = useConsoleFetch(
    async () => {
      if (!cityId) return [];
      const res = await consoleApi.clubs.list({ city_id: cityId, limit: 200 });
      return Array.isArray(res) ? res : [];
    },
    [cityId],
    { refetchOnWindowFocus: false }
  );

  const { data: meetingsRaw, refetch: refetchMeetings } = useConsoleFetch(
    async () => {
      if (!cityId) return [];
      const res = await consoleApi.meetings.list({ city_id: cityId, limit: 500 });
      return Array.isArray(res) ? res : [];
    },
    [cityId],
    { refetchOnWindowFocus: false }
  );

  const { data: detailClub, loading: detailClubLoading, error: detailClubError, refetch: refetchDetailClub } = useConsoleFetch(
    () => (clubIdFromPath ? consoleApi.clubs.get(clubIdFromPath) : Promise.resolve(null)),
    [clubIdFromPath],
    { refetchOnWindowFocus: false }
  );

  const handleOrganizerClaimIssued = useCallback(() => {
    refetchDetailClub();
    refetchClubs();
    loadClaimMetrics();
  }, [refetchDetailClub, refetchClubs, loadClaimMetrics]);

  const clubs = useMemo(() => clubsRaw ?? [], [clubsRaw]);
  const runs = useMemo(() => meetingsRaw ?? [], [meetingsRaw]);

  const useSouthFloridaRegions = isSouthFloridaCity(cityId);
  const useGreaterLaRegions = isGreaterLosAngelesCity(cityId);
  const regions = useMemo(
    () => getConsoleRegionOptions(cityId, clubs),
    [cityId, clubs]
  );

  const filtered = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    const list = clubs.filter((c) => {
      if (
        filterRegion &&
        getClubRegionForFilter(c, useSouthFloridaRegions, useGreaterLaRegions) !== filterRegion
      )
        return false;
      if (filterClaim && c.claim_status !== filterClaim) return false;
      if (filterCompleteness) {
        const p = clubCompleteness(c, runs);
        if (filterCompleteness === "complete" && p < 70) return false;
        if (filterCompleteness === "partial" && (p < 40 || p >= 70)) return false;
        if (filterCompleteness === "incomplete" && p >= 40) return false;
      }
      if (searchLower) {
        const hay = `${c.name ?? ""} ${c.city ?? ""} ${c.description ?? ""}`.toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    });
    // Incomplete clubs first (ascending completion %) so "not fully done" appear at top
    return [...list].sort((a, b) => clubCompleteness(a, runs) - clubCompleteness(b, runs));
  }, [
    clubs,
    runs,
    search,
    filterRegion,
    filterClaim,
    filterCompleteness,
    useSouthFloridaRegions,
    useGreaterLaRegions,
  ]);

  const handleToggleRecurringRunsVary = useCallback(
    async (club, value) => {
      try {
        await consoleApi.clubs.update(club.id, { recurring_runs_vary: value });
        toast.success(value ? "Runs vary" : "Same weekly");
        refetchClubs();
      } catch (e) {
        toast.error(e?.response?.data?.detail ?? e?.message ?? "Failed to update");
      }
    },
    [refetchClubs]
  );

  const handleToggleAnchorClub = useCallback(
    async (club, value) => {
      try {
        await consoleApi.clubs.update(club.id, { is_anchor_club: value });
        toast.success(value ? "Marked as anchor club." : "Anchor cleared.");
        refetchClubs();
        onQualityMetricsChange?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail ?? e?.message ?? "Failed to update");
      }
    },
    [refetchClubs, onQualityMetricsChange]
  );

  const getEmptyOnboardForm = useCallback(() => {
    const prefilledCity = cityId && clubs.length > 0 ? (clubs[0].city ?? "") : "";
    return { ...INITIAL_ONBOARD_FORM, city: prefilledCity };
  }, [cityId, clubs]);

  const openAddClubModal = useCallback(() => {
    const empty = getEmptyOnboardForm();
    setAddClubForm(empty);
    setHeroImageFile(null);
    setProfileImageFile(null);
    setCarouselImageFiles([]);
    setAddClubHeroPasteUrl("");
    setAddClubProfilePasteUrl("");
    setAddClubCarouselPasteUrls([]);
    addClubOpenBaselineRef.current = {
      formJson: JSON.stringify(empty),
      carouselSig: serializeFileArrayForDirty([]),
      pasteSig: serializeAddClubPasteUrlsForDirty("", "", []),
    };
    setShowAddClubModal(true);
  }, [getEmptyOnboardForm]);

  const closeAddClubModal = useCallback(() => {
    setUnsavedClubModalKind(null);
    setShowAddClubModal(false);
    setShowOnboardPassword(false);
    setHeroImageFile(null);
    setProfileImageFile(null);
    setCarouselImageFiles([]);
    setAddClubHeroPasteUrl("");
    setAddClubProfilePasteUrl("");
    setAddClubCarouselPasteUrls([]);
    setAddClubForm(INITIAL_ONBOARD_FORM);
    addClubOpenBaselineRef.current = null;
  }, []);

  const closeEditClubModal = useCallback(() => {
    setUnsavedClubModalKind(null);
    setEditModalClub(null);
    setEditFullClub(null);
    setEditForm({ ...INITIAL_EDIT_FORM });
    setEditHeroFile(null);
    setEditProfileFile(null);
    setEditHeroPasteUrl("");
    setEditProfilePasteUrl("");
    setEditCarouselSlots([]);
    setEditScrapedImageRemovingUrl(null);
    setEditClubPasteFieldSaving(null);
    editClubOpenBaselineRef.current = null;
  }, []);

  const isAddClubDirty = useCallback(() => {
    const b = addClubOpenBaselineRef.current;
    if (!showAddClubModal || !b) return false;
    return (
      JSON.stringify(addClubForm) !== b.formJson ||
      heroImageFile != null ||
      profileImageFile != null ||
      serializeFileArrayForDirty(carouselImageFiles) !== b.carouselSig ||
      serializeAddClubPasteUrlsForDirty(addClubHeroPasteUrl, addClubProfilePasteUrl, addClubCarouselPasteUrls) !==
        b.pasteSig
    );
  }, [
    showAddClubModal,
    addClubForm,
    heroImageFile,
    profileImageFile,
    carouselImageFiles,
    addClubHeroPasteUrl,
    addClubProfilePasteUrl,
    addClubCarouselPasteUrls,
  ]);

  const isEditClubDirty = useCallback(() => {
    if (editFormLoading) return false;
    const b = editClubOpenBaselineRef.current;
    if (!editModalClub || !b) return false;
    return (
      JSON.stringify(editForm) !== b.formJson ||
      editHeroFile != null ||
      editProfileFile != null ||
      (editHeroPasteUrl ?? "").trim() !== (b.heroPasteSig ?? "") ||
      (editProfilePasteUrl ?? "").trim() !== (b.profilePasteSig ?? "") ||
      serializeClubEditCarouselSlotsForDirty(editCarouselSlots) !== b.slotsSig
    );
  }, [
    editFormLoading,
    editModalClub,
    editForm,
    editHeroFile,
    editProfileFile,
    editHeroPasteUrl,
    editProfilePasteUrl,
    editCarouselSlots,
  ]);

  const tryCloseAddClubModal = useCallback(() => {
    if (!isAddClubDirty()) {
      closeAddClubModal();
      return;
    }
    setUnsavedClubModalKind("add");
  }, [isAddClubDirty, closeAddClubModal]);

  const tryCloseEditClubModal = useCallback(() => {
    if (editFormLoading) {
      closeEditClubModal();
      return;
    }
    if (!isEditClubDirty()) {
      closeEditClubModal();
      return;
    }
    setUnsavedClubModalKind("edit");
  }, [editFormLoading, isEditClubDirty, closeEditClubModal]);

  const openEditClubModal = useCallback(
    async (club) => {
      if (!club?.id) return;
      setEditModalClub(club);
      setEditClubPasteFieldSaving(null);
      editClubOpenBaselineRef.current = null;
      setEditFormLoading(true);
      setEditHeroFile(null);
      setEditProfileFile(null);
      setEditHeroPasteUrl("");
      setEditProfilePasteUrl("");
      setEditCarouselSlots([]);
      setEditFullClub(null);
      setEditScrapedImageRemovingUrl(null);
      try {
        const fullClub = await consoleApi.clubs.get(club.id);
        setEditFullClub(fullClub);
        const normalizedCarousel = normalizeExistingImages(fullClub.clubcard_images || []);
        const slotList = normalizedCarousel.map((img, i) => ({
          id: `erc-${i}-${String(img.url).slice(-48).replace(/[^\w]/g, "")}`,
          kind: "remote",
          url: img.url,
        }));
        setEditCarouselSlots(slotList);
        const notes = Array.isArray(fullClub.club_notes) ? fullClub.club_notes : [];
        const paddedNotes = [notes[0] ?? "", notes[1] ?? "", notes[2] ?? ""];
        const formSnapshot = {
          run_club_name: fullClub.name ?? "",
          city: fullClub.city ?? "",
          state: fullClub.state ?? "",
          country: fullClub.country ?? "",
          type: fullClub.type ?? "",
          description: fullClub.description ?? "",
          skill_level: fullClub.skill_level ?? "All levels",
          home_base_address: fullClub.home_base_address ?? "",
          home_base_description: fullClub.home_base_description ?? "",
          latitude: fullClub.latitude ?? null,
          longitude: fullClub.longitude ?? null,
          paces: paceLabelsToCodes(fullClub.paces),
          club_notes: paddedNotes,
          recurring_runs_vary: fullClub.recurring_runs_vary ?? false,
          is_anchor_club: fullClub.is_anchor_club ?? false,
          internal_notes: fullClub.internal_notes ?? "",
          ...Object.fromEntries(Object.keys(CLUB_EDITABLE_LINK_FIELDS).map((k) => [k, fullClub[k] != null ? String(fullClub[k]) : ""])),
        };
        setEditForm(formSnapshot);
        editClubOpenBaselineRef.current = {
          formJson: JSON.stringify(formSnapshot),
          slotsSig: serializeClubEditCarouselSlotsForDirty(slotList),
          heroPasteSig: "",
          profilePasteSig: "",
        };
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to load club"));
        setEditModalClub(null);
        editClubOpenBaselineRef.current = null;
      } finally {
        setEditFormLoading(false);
      }
    },
    []
  );

  const openInternalNotesModal = useCallback((club) => {
    if (club?.id) setInternalNotesModalClub(club);
  }, []);

  const closeInternalNotesModal = useCallback(() => {
    setInternalNotesModalClub(null);
  }, []);

  const handleInternalNotesSave = useCallback(async () => {
    if (!internalNotesModalClub?.id) return;
    setInternalNotesSaving(true);
    try {
      const value = (internalNotesValue ?? "").trim().slice(0, INTERNAL_NOTES_MAX_LENGTH) || null;
      await consoleApi.clubs.update(internalNotesModalClub.id, { internal_notes: value });
      toast.success("Internal notes saved.");
      await refetchClubs();
      if (detailClub?.id === internalNotesModalClub.id) refetchDetailClub();
      setInternalNotesModalClub(null);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to save internal notes"));
    } finally {
      setInternalNotesSaving(false);
    }
  }, [internalNotesModalClub, internalNotesValue, detailClub?.id, refetchClubs, refetchDetailClub]);

  /** Upload file to GCP, PATCH club field; returns public URL or null if no file. */
  const uploadClubImageAndSet = useCallback(async (clubId, file, field) => {
    if (!file) return null;
    const { url } = await consoleApi.uploadClubImage(clubId, file);
    if (!url) throw new Error("No URL returned from image upload");
    await consoleApi.clubs.update(clubId, { [field]: url });
    return url;
  }, []);

  /**
   * Upload carousel files and/or import pasted HTTPS URLs to GCP, merge with existing, then PATCH clubcard_images.
   */
  const uploadClubCarouselAndSet = useCallback(async (clubId, files, existingImages = [], pasteUrlList = []) => {
    const fileList = Array.isArray(files) ? files : [];
    const pastes = Array.isArray(pasteUrlList) ? pasteUrlList : [];
    if (fileList.length === 0 && pastes.length === 0) return;
    const existing = normalizeExistingImages(existingImages);
    const newUrls = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!file) continue;
      try {
        const { url } = await consoleApi.uploadClubImage(clubId, file);
        if (url) newUrls.push(url);
      } catch (e) {
        toast.error(getApiErrorMessage(e, `Carousel image ${i + 1} upload failed`));
      }
    }
    for (let i = 0; i < pastes.length; i++) {
      const u = String(pastes[i] ?? "").trim();
      if (!u.startsWith("http")) continue;
      try {
        const { url } = await consoleApi.uploadClubImageFromUrl(clubId, u);
        if (url) newUrls.push(url);
      } catch (e) {
        toast.error(getApiErrorMessage(e, `Carousel image URL ${i + 1} import failed`));
      }
    }
    const combined = buildImagesArrayFromUrls([
      ...existing.map((img) => img.url),
      ...newUrls,
    ]).slice(0, MAX_CAROUSEL_IMAGES);
    if (combined.length > 0) {
      await consoleApi.clubs.update(clubId, { clubcard_images: combined });
    }
  }, []);

  const handleAddClubSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const pre = validateConsoleOnboardPrerequisites(addClubForm);
      if (!pre.ok) {
        toast.error(pre.errorMessage);
        return;
      }
      setAddClubSubmitting(true);
      try {
        const payload = buildOnboardPayload(addClubForm, cityId);
        const result = await consoleApi.onboardClub(payload);
        const newClubId = result?.club_id;
        if (newClubId) {
          let heroUrl = null;
          if (heroImageFile) {
            heroUrl = await uploadClubImageAndSet(newClubId, heroImageFile, "hero_image");
          } else {
            const hp = (addClubHeroPasteUrl ?? "").trim();
            if (hp.startsWith("http")) {
              const { url } = await consoleApi.uploadClubImageFromUrl(newClubId, hp);
              if (!url) throw new Error("No URL returned from hero image import");
              await consoleApi.clubs.update(newClubId, { hero_image: url });
              heroUrl = url;
            }
          }
          if (profileImageFile) {
            await uploadClubImageAndSet(newClubId, profileImageFile, "profile_picture");
          } else {
            const pp = (addClubProfilePasteUrl ?? "").trim();
            if (pp.startsWith("http")) {
              const { url } = await consoleApi.uploadClubImageFromUrl(newClubId, pp);
              if (url) await consoleApi.clubs.update(newClubId, { profile_picture: url });
            }
          }
          const carouselSeed = heroUrl ? [{ url: heroUrl }] : [];
          await uploadClubCarouselAndSet(
            newClubId,
            carouselImageFiles,
            carouselSeed,
            addClubCarouselPasteUrls
          );
        }
        toast.success("Club onboarded. Organizer can log in with email and password.");
        notifyIfOnboardCredentialsSheetFailed(result, toast);
        refetchClubs();
        closeAddClubModal();
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to onboard club"));
      } finally {
        setAddClubSubmitting(false);
      }
    },
    [
      addClubForm,
      cityId,
      heroImageFile,
      profileImageFile,
      carouselImageFiles,
      addClubHeroPasteUrl,
      addClubProfilePasteUrl,
      addClubCarouselPasteUrls,
      uploadClubImageAndSet,
      uploadClubCarouselAndSet,
      refetchClubs,
      closeAddClubModal,
    ]
  );

  const handleEditClubSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!editModalClub?.id) return;
      const name = (editForm.run_club_name ?? "").trim();
      const city = (editForm.city ?? "").trim();
      if (!name || !city) {
        toast.error("Club name and city are required.");
        return;
      }
      setEditSubmitting(true);
      try {
        const payload = buildEditPayload(editForm, cityId);
        await consoleApi.clubs.update(editModalClub.id, payload);
        if (editHeroFile) {
          await uploadClubImageAndSet(editModalClub.id, editHeroFile, "hero_image");
        } else {
          const hp = (editHeroPasteUrl ?? "").trim();
          if (hp.startsWith("http")) {
            const { url } = await consoleApi.uploadClubImageFromUrl(editModalClub.id, hp);
            if (url) await consoleApi.clubs.update(editModalClub.id, { hero_image: url });
          }
        }
        if (editProfileFile) {
          await uploadClubImageAndSet(editModalClub.id, editProfileFile, "profile_picture");
        } else {
          const pp = (editProfilePasteUrl ?? "").trim();
          if (pp.startsWith("http")) {
            const { url } = await consoleApi.uploadClubImageFromUrl(editModalClub.id, pp);
            if (url) await consoleApi.clubs.update(editModalClub.id, { profile_picture: url });
          }
        }
        const carouselUrls = await collectUrlsFromCarouselSlots(
          editCarouselSlots,
          (file) => consoleApi.uploadClubImage(editModalClub.id, file),
          {
            onUploadError: (_, uploadErr) => {
              toast.error(getApiErrorMessage(uploadErr, "Carousel image upload failed"));
            },
            uploadPastedUrl: (imageUrl) =>
              consoleApi.uploadClubImageFromUrl(editModalClub.id, imageUrl),
          }
        );
        const carouselPayload = buildImagesArrayFromUrls(carouselUrls).slice(0, MAX_CAROUSEL_IMAGES);
        await consoleApi.clubs.update(editModalClub.id, {
          clubcard_images: carouselPayload.length > 0 ? carouselPayload : [],
        });
        toast.success("Club updated.");
        refetchClubs();
        onQualityMetricsChange?.();
        closeEditClubModal();
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to update club"));
      } finally {
        setEditSubmitting(false);
      }
    },
    [
      editModalClub,
      editForm,
      cityId,
      editHeroFile,
      editProfileFile,
      editHeroPasteUrl,
      editProfilePasteUrl,
      editCarouselSlots,
      uploadClubImageAndSet,
      refetchClubs,
      closeEditClubModal,
      onQualityMetricsChange,
    ]
  );

  const saveEditClubHeroPasteUrl = useCallback(async () => {
    if (!editModalClub?.id) return;
    if (editHeroFile) {
      toast.error("Remove the new hero file to save a pasted URL.");
      return;
    }
    const hp = (editHeroPasteUrl ?? "").trim();
    if (!hp.startsWith("http")) {
      toast.error("Enter a valid HTTPS image URL.");
      return;
    }
    setEditClubPasteFieldSaving("hero");
    try {
      const { url } = await consoleApi.uploadClubImageFromUrl(editModalClub.id, hp);
      if (!url) throw new Error("No URL returned from hero image import");
      await consoleApi.clubs.update(editModalClub.id, { hero_image: url });
      setEditModalClub((prev) => (prev ? { ...prev, hero_image: url } : null));
      setEditFullClub((prev) => (prev ? { ...prev, hero_image: url } : null));
      setEditHeroPasteUrl("");
      refetchClubs();
      onQualityMetricsChange?.();
      toast.success("Hero image saved.");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to save hero image"));
    } finally {
      setEditClubPasteFieldSaving(null);
    }
  }, [editModalClub, editHeroFile, editHeroPasteUrl, refetchClubs, onQualityMetricsChange]);

  const saveEditClubProfilePasteUrl = useCallback(async () => {
    if (!editModalClub?.id) return;
    if (editProfileFile) {
      toast.error("Remove the new profile file to save a pasted URL.");
      return;
    }
    const pp = (editProfilePasteUrl ?? "").trim();
    if (!pp.startsWith("http")) {
      toast.error("Enter a valid HTTPS image URL.");
      return;
    }
    setEditClubPasteFieldSaving("profile");
    try {
      const { url } = await consoleApi.uploadClubImageFromUrl(editModalClub.id, pp);
      if (!url) throw new Error("No URL returned from profile image import");
      await consoleApi.clubs.update(editModalClub.id, { profile_picture: url });
      setEditModalClub((prev) => (prev ? { ...prev, profile_picture: url } : null));
      setEditFullClub((prev) => (prev ? { ...prev, profile_picture: url } : null));
      setEditProfilePasteUrl("");
      refetchClubs();
      onQualityMetricsChange?.();
      toast.success("Profile picture saved.");
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Failed to save profile picture"));
    } finally {
      setEditClubPasteFieldSaving(null);
    }
  }, [editModalClub, editProfileFile, editProfilePasteUrl, refetchClubs, onQualityMetricsChange]);

  /** Import pasted carousel URL to storage, merge onto saved clubcard_images, refresh slots baseline. */
  const saveEditClubCarouselPasteUrl = useCallback(
    async (draftRaw) => {
      const u = (draftRaw ?? "").trim();
      if (!editModalClub?.id) return false;
      if (!editFullClub) {
        toast.error("Club still loading.");
        return false;
      }
      if (!u.startsWith("http")) {
        toast.error("Enter a valid HTTPS image URL.");
        return false;
      }
      if (editCarouselSlots.length >= MAX_CAROUSEL_IMAGES) {
        toast.error(`At most ${MAX_CAROUSEL_IMAGES} carousel images.`);
        return false;
      }
      setEditClubPasteFieldSaving("carousel");
      try {
        const { url: stored } = await consoleApi.uploadClubImageFromUrl(editModalClub.id, u);
        if (!stored) throw new Error("No URL returned from carousel image import");
        const prevUrls = normalizeExistingImages(editFullClub.clubcard_images || []).map((img) => img.url);
        const mergedUrls = dedupeImageUrlsPreserveOrder([...prevUrls, stored]).slice(0, MAX_CAROUSEL_IMAGES);
        if (mergedUrls.length === prevUrls.length) {
          toast.info("That image is already on the carousel.");
          return true;
        }
        await consoleApi.clubs.update(editModalClub.id, {
          clubcard_images: buildImagesArrayFromUrls(mergedUrls),
        });
        const updated = await consoleApi.clubs.get(editModalClub.id);
        setEditFullClub(updated);
        setEditCarouselSlots((prev) => {
          const hasSlot = prev.some((s) => {
            if (s.kind !== "remote" && s.kind !== "pasted_url" && s.kind !== "url") return false;
            const su = s.url != null ? String(s.url).trim() : "";
            if (!su.startsWith("http")) return false;
            return normalizeImageUrlForDedupe(su) === normalizeImageUrlForDedupe(stored);
          });
          if (hasSlot) {
            if (editClubOpenBaselineRef.current) {
              editClubOpenBaselineRef.current.slotsSig = serializeClubEditCarouselSlotsForDirty(prev);
            }
            return prev;
          }
          if (prev.length >= MAX_CAROUSEL_IMAGES) {
            if (editClubOpenBaselineRef.current) {
              editClubOpenBaselineRef.current.slotsSig = serializeClubEditCarouselSlotsForDirty(prev);
            }
            return prev;
          }
          const next = [
            ...prev,
            {
              id: `erc-saved-${Date.now()}`,
              kind: "remote",
              url: stored,
            },
          ];
          if (editClubOpenBaselineRef.current) {
            editClubOpenBaselineRef.current.slotsSig = serializeClubEditCarouselSlotsForDirty(next);
          }
          return next;
        });
        refetchClubs();
        onQualityMetricsChange?.();
        toast.success("Carousel image saved.");
        return true;
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to save carousel image"));
        return false;
      } finally {
        setEditClubPasteFieldSaving(null);
      }
    },
    [
      editModalClub,
      editFullClub,
      editCarouselSlots,
      refetchClubs,
      onQualityMetricsChange,
    ]
  );

  const openUpcomingRunsModal = useCallback((club) => {
    if (!club?.id) return;
    setUpcomingRunsModalClub(club);
  }, []);

  useEffect(() => {
    if (!upcomingRunsModalClub?.id) return;
    const clubId = upcomingRunsModalClub.id;
    let cancelled = false;
    setUpcomingRunsList([]);
    setUpcomingRunsLoading(true);
    (async () => {
      try {
        const params = { club_id: clubId, limit: 100 };
        if (meetingsRange && meetingsRange !== "all") params.range_key = meetingsRange;
        const list = await consoleApi.meetings.list(params);
        if (!cancelled) setUpcomingRunsList(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          toast.error(getApiErrorMessage(e, "Failed to load upcoming runs"));
          setUpcomingRunsList([]);
        }
      } finally {
        if (!cancelled) setUpcomingRunsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meetingsRange, upcomingRunsModalClub?.id]);

  const closeUpcomingRunsModal = useCallback(() => {
    setUpcomingRunsModalClub(null);
    setUpcomingRunsList([]);
    setUpcomingRunsLoading(false);
  }, []);

  const openRunsListModal = useCallback((club, mode) => {
    if (!club?.id) return;
    setRunsListModal({ club, mode });
  }, []);

  const closeRunsListModal = useCallback(() => {
    setRunsListModal(null);
  }, []);

  const membersFetchClubIdRef = useRef(null);
  const openMembersModal = useCallback((club) => {
    if (!club?.id) return;
    const clubId = club.id;
    membersFetchClubIdRef.current = clubId;
    setMembersModalClub(club);
    setMembersList([]);
    setMembersTotal(0);
    setMembersError(false);
    setMembersLoading(true);
    consoleApi.clubs
      .getMembers(clubId, 200, 0)
      .then((data) => {
        if (membersFetchClubIdRef.current !== clubId) return;
        setMembersError(false);
        setMembersList(Array.isArray(data.members) ? data.members : []);
        setMembersTotal(typeof data.total === "number" ? data.total : 0);
      })
      .catch(() => {
        if (membersFetchClubIdRef.current !== clubId) return;
        setMembersError(true);
      })
      .finally(() => setMembersLoading(false));
  }, []);

  const handleVerificationChange = useCallback(
    async (clubId, adminVerified) => {
      try {
        await consoleApi.clubs.update(clubId, { admin_verified: adminVerified });
        await Promise.all([refetchDetailClub(), refetchClubs()]);
        toast.success(adminVerified ? "Club marked as verified." : "Verification cleared.");
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to update verification"));
      }
    },
    [refetchDetailClub, refetchClubs]
  );

  const handleDeleteClub = useCallback(
    async (club) => {
      if (!club?.id) return;
      setClubDeleteBusy(true);
      try {
        await consoleApi.clubs.delete(club.id);
        toast.success("Club deleted (organizer account and related data removed).");
        navigate(getConsoleCityTabPath("clubs", cityId, meetingsRange));
        await Promise.all([refetchClubs(), refetchMeetings()]);
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to delete club"));
        throw e;
      } finally {
        setClubDeleteBusy(false);
      }
    },
    [cityId, meetingsRange, navigate, refetchClubs, refetchMeetings]
  );

  /** Clear an editable club URL when the stored value is wrong (PATCH null). */
  const handleRemoveClubLink = useCallback(
    async (club, field) => {
      if (!club?.id || !isClubEditableLinkField(field)) return;
      const label = CLUB_EDITABLE_LINK_FIELDS[field].label;
      if (!window.confirm(`Remove ${label} URL for "${club.name || club.id}"?`)) return;
      try {
        await consoleApi.clubs.update(club.id, { [field]: null });
        toast.success(`${label} link removed.`);
        await Promise.all([refetchDetailClub(), refetchClubs()]);
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to remove link"));
      }
    },
    [refetchDetailClub, refetchClubs]
  );

  /** Set a club link field to a new URL (PATCH). Throws on failure so the inline editor can stay open. */
  const handleReplaceClubLink = useCallback(
    async (club, field, newUrl) => {
      if (!club?.id || !isClubEditableLinkField(field)) return;
      const trimmed = (newUrl || "").trim();
      const maxLen = CLUB_EDITABLE_LINK_FIELDS[field].maxLen;
      if (!trimmed) {
        toast.error("Enter a URL.");
        throw new Error("validation");
      }
      if (trimmed.length > maxLen) {
        toast.error(`URL must be at most ${maxLen} characters.`);
        throw new Error("validation");
      }
      try {
        await consoleApi.clubs.update(club.id, { [field]: trimmed });
        toast.success(`${CLUB_EDITABLE_LINK_FIELDS[field].label} URL updated.`);
        await Promise.all([refetchDetailClub(), refetchClubs()]);
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Failed to save link"));
        throw e;
      }
    },
    [refetchDetailClub, refetchClubs]
  );

  const closeMembersModal = useCallback(() => {
    membersFetchClubIdRef.current = null;
    setMembersModalClub(null);
    setMembersList([]);
    setMembersTotal(0);
    setMembersError(false);
  }, []);

  /** Open Create Run flow on Runs tab with this club pre-selected and home-base spot pre-filled. */
  const openAddRunForClub = useCallback(
    (club) => {
      if (!club?.id) return;
      const cityForRuns = club.city_id || cityId;
      const prefilledForm = {
        club_id: club.id,
        address: (club.home_base_address ?? "").trim() || "",
        city: (club.city ?? "").trim() || "",
        state: (club.state ?? "").trim() || "",
        status: "upcoming",
        recurrence: "weekly",
      };
      navigate(getConsoleCityTabPath("runs", cityForRuns, meetingsRange), {
        state: { openAddRunModalWithPrefill: true, prefilledForm },
      });
    },
    [cityId, meetingsRange, navigate]
  );

  const hasCity = Boolean(cityId);
  const isDetailMode = Boolean(clubIdFromPath);

  useEffect(() => {
    if (!clubIdFromPath) setDetailCarouselPreviewUrl(null);
  }, [clubIdFromPath]);

  const renderMainContent = () => {
    if (isDetailMode) {
      if (detailClubLoading) {
        return (
          <div className="console-main-inner" style={{ padding: "2rem", textAlign: "center" }}>
            <p className="console-muted-text">Loading club…</p>
          </div>
        );
      }
      if (detailClubError || !detailClub) {
        return (
          <div className="console-main-inner" style={{ padding: "2rem", textAlign: "center" }}>
            <p className="console-error-text">{detailClubError || "Club not found."}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => navigate(getConsoleCityTabPath("clubs", cityId, meetingsRange))}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5 inline" aria-hidden />
              Back to Clubs
            </Button>
          </div>
        );
      }
      return (
        <ClubDetail
          club={detailClub}
          runs={runs}
          cityId={cityId}
          meetingsRange={meetingsRange}
          detailCarouselPreviewUrl={detailCarouselPreviewUrl}
          setDetailCarouselPreviewUrl={setDetailCarouselPreviewUrl}
          onBack={() => navigate(getConsoleCityTabPath("clubs", cityId, meetingsRange))}
          onEdit={openEditClubModal}
          onInternalNotes={openInternalNotesModal}
          onAddRun={() => openAddRunForClub(detailClub)}
          onUpcomingRuns={openUpcomingRunsModal}
          onRunsList={openRunsListModal}
          onMembers={openMembersModal}
          onVerificationChange={handleVerificationChange}
          onRemoveClubLink={handleRemoveClubLink}
          onReplaceClubLink={handleReplaceClubLink}
          onDeleteClub={handleDeleteClub}
          clubDeleteBusy={clubDeleteBusy}
          onOrganizerClaimIssued={handleOrganizerClaimIssued}
        />
      );
    }
    if (!hasCity) {
      return (
        <div className="console-main-inner" style={{ padding: "2rem", maxWidth: 520, margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: "var(--console-ink)", fontSize: 15, marginBottom: 12 }}>
            Choose a city context to view and manage clubs. The Network grid only lists cities that already have clubs.
          </p>
          <p style={{ color: "var(--console-muted)", fontSize: 14, marginBottom: 20 }}>
            For a new market, add a city on the Cities page, then use <strong>Open clubs</strong> on that row (or a <code className="text-xs">?city=</code> link) so onboarding attaches <code className="text-xs">city_id</code>.
          </p>
          <Link
            to="/console/cities"
            className="tb-btn primary inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium"
          >
            Go to Cities
          </Link>
        </div>
      );
    }
    return (
      <>
        <ConsolePageHeader
          label="City Seeding"
          title="Clubs"
          sub="Clubs in this city. Filter and search; click a club to view. All data from live API."
        />
        <div className="filter-bar clubs-toolbar">
          <input
            type="text"
            placeholder="Search clubs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search clubs"
            className="filter-sel"
            style={{ minWidth: 180 }}
          />
          <select value={filterRegion} onChange={(e) => setFilterRegion(e.target.value)} aria-label="Filter by region" className="filter-sel">
            <option value="">All regions</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <select value={filterClaim} onChange={(e) => setFilterClaim(e.target.value)} aria-label="Filter by claim state" className="filter-sel">
            <option value="">All claim states</option>
            <option value="internal">Internal only</option>
            <option value="invited">Invited to claim</option>
            <option value="claimed">Claimed</option>
          </select>
          <select value={filterCompleteness} onChange={(e) => setFilterCompleteness(e.target.value)} aria-label="Filter by completeness" className="filter-sel">
            <option value="">All completeness</option>
            <option value="complete">Complete (70%+)</option>
            <option value="partial">Partial (40–69%)</option>
            <option value="incomplete">Incomplete (&lt;40%)</option>
          </select>
          <div style={{ flex: 1 }} />
          <Button size="sm" className="bg-[#1A1916] hover:bg-[#3C3830] text-white" onClick={openAddClubModal}>
            + Add Club
          </Button>
        </div>
        {cityId && (claimMetricsLoading || claimMetrics) ? (
          <div
            style={{
              fontSize: 13,
              color: "var(--console-muted)",
              margin: "0 0 12px 4px",
              lineHeight: 1.5,
            }}
          >
            {claimMetricsLoading ? (
              "Loading organizer claim metrics…"
            ) : (
              <>
                <strong style={{ color: "var(--console-ink)" }}>Organizer claim</strong> — claimed:{" "}
                {claimMetrics.by_status?.claimed ?? 0} · invited: {claimMetrics.by_status?.invited ?? 0} · internal:{" "}
                {claimMetrics.by_status?.internal ?? 0}
                {(claimMetrics.by_status?.unset ?? 0) > 0 ? ` · unset: ${claimMetrics.by_status.unset}` : ""} · total:{" "}
                {claimMetrics.total ?? 0}
              </>
            )}
          </div>
        ) : null}
        <div className="clubs-grid">
          {filtered.length === 0 ? (
              <p style={{ fontSize: 12, color: "var(--console-muted)", padding: "24px 0", gridColumn: "1 / -1" }}>
                {clubs.length === 0 ? "No clubs found." : "No clubs match filters."}
              </p>
            ) : (
              filtered.map((c) => {
                const { pct, upcomingRunCount, completedRunCount, compFields, fillColor, missing, showMissing, missingLineColor } = getClubDetailStats(
                  c,
                  runs,
                  meetingsRange
                );
                const claimStatus = c.claim_status;

                return (
                  <div
                    key={c.id}
                    className="club-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(getConsoleClubUrl(cityId, c.id, meetingsRange))}
                    onKeyDown={(e) => e.key === "Enter" && (navigate(getConsoleClubUrl(cityId, c.id, meetingsRange)), e.preventDefault())}
                  >
                    <div className="club-hero">
                      {c.hero_image ? (
                        <img src={c.hero_image} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="club-hero-placeholder">No hero image</div>
                      )}
                      <div className="club-logo">
                        {c.profile_picture ? (
                          <img src={c.profile_picture} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                        ) : (
                          <div className="club-logo-placeholder">{c.name ? c.name.charAt(0) : ""}</div>
                        )}
                      </div>
                    </div>
                    <div className="club-body">
                      <div className="flex items-center justify-between gap-2" style={{ marginBottom: 4 }}>
                        <div className="club-name" style={{ flex: "1", minWidth: 0 }}>{c.name}</div>
                        <div
                          className="flex items-center gap-3 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              id={`recurring-vary-${c.id}`}
                              checked={!!c.recurring_runs_vary}
                              onChange={(e) => handleToggleRecurringRunsVary(c, e.target.checked)}
                              className="rounded border-[var(--console-border)] cursor-pointer"
                              title="Weekly runs vary in location or type"
                            />
                            <label htmlFor={`recurring-vary-${c.id}`} className="text-xs font-medium cursor-pointer whitespace-nowrap" style={{ color: "var(--console-ink)" }}>
                              Runs vary
                            </label>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              id={`anchor-club-${c.id}`}
                              checked={c.is_anchor_club === true}
                              onChange={(e) => handleToggleAnchorClub(c, e.target.checked)}
                              className="rounded border-[var(--console-border)] cursor-pointer"
                              title="One of the strongest, best-known clubs in the city"
                            />
                            <label htmlFor={`anchor-club-${c.id}`} className="text-xs font-medium cursor-pointer whitespace-nowrap" style={{ color: "var(--console-ink)" }}>
                              Anchor
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="club-region">{c.region ?? c.city}</div>
                      {(c.home_base_address && c.home_base_address.trim()) ? (
                        <div className="club-meetup-spot text-xs mt-1" style={{ color: "var(--console-muted)", lineHeight: 1.4 }}>
                          <span className="font-medium" style={{ color: "var(--console-ink)" }}>Meetup:</span> {c.home_base_address.trim()}
                        </div>
                      ) : (
                        <div className="club-meetup-spot text-xs mt-1" style={{ color: "var(--console-muted)", fontStyle: "italic" }}>No meetup spot set</div>
                      )}
                      <div className="club-meta-row" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="club-runs-count"
                          title={`View upcoming runs (${getConsoleRangeLabel(meetingsRange)})`}
                          onClick={() => openRunsListModal(c, "upcoming")}
                        >
                          {upcomingRunCount} Upcoming Run{upcomingRunCount !== 1 ? "s" : ""}
                        </button>
                        <button
                          type="button"
                          className="club-runs-count club-runs-count-completed"
                          title="View completed runs"
                          onClick={() => openRunsListModal(c, "completed")}
                        >
                          {completedRunCount} Completed Run{completedRunCount !== 1 ? "s" : ""}
                        </button>
                        {claimStatus && (
                          <span className={`claim-pill claim-${claimStatus}`}>{CLAIM_LABEL[claimStatus] ?? claimStatus}</span>
                        )}
                        {c.admin_verified && (
                          <span className="claim-pill claim-verified" title="Admin verified">Verified</span>
                        )}
                        {c.is_anchor_club === true && (
                          <span className="claim-pill claim-anchor" title="One of the strongest, best-known clubs in the city">Anchor</span>
                        )}
                      </div>
                      <div className={`comp-dots ${pct === 100 ? "comp-dots--full" : ""}`} title="Dots: hero image · about · good-to-know · meet-up · run verified">
                        {compFields.map((f) => (
                          <span key={f.key} className={`cdot ${f.ok ? "yes" : "no"}`} title={f.key} aria-hidden />
                        ))}
                      </div>
                      <div className={`completeness-row ${pct === 100 ? "completeness-row--full" : ""}`} title="Club completeness. 70%+ = ready for app.">
                        <div className="comp-track">
                          <div className="comp-fill" style={{ width: `${pct}%`, background: fillColor }} />
                        </div>
                        <div className="comp-pct">{pct}%</div>
                      </div>
                      {showMissing && (
                        <div style={{ fontSize: 9, color: missingLineColor, marginBottom: 6, lineHeight: 1.65 }}>
                          <span style={{ fontWeight: 600 }}>100%:</span>{" "}
                          {missing.map((m) => `- ${m}`).join(" · ")}
                        </div>
                      )}
                      <div className="club-card-actions flex flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditClubModal(c);
                            }}
                          >
                            Edit Club
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openInternalNotesModal(c);
                            }}
                          >
                            Internal notes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[var(--console-accent)] border-[var(--console-accent)]"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAddRunForClub(c);
                            }}
                          >
                            + Add Run
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpcomingRunsModal(c);
                            }}
                            title={`View upcoming runs (${getConsoleRangeLabel(meetingsRange)})`}
                          >
                            Upcoming runs
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
      </>
    );
  };

  return (
    <ConsoleViewShell
      loading={!isDetailMode && hasCity && loading}
      error={!isDetailMode ? error : null}
      loadingLabel="Loading clubs…"
    >
      {renderMainContent()}
      <ClubsOnboardClubModal
        open={showAddClubModal}
        onRequestClose={tryCloseAddClubModal}
        addClubForm={addClubForm}
        setAddClubForm={setAddClubForm}
        addClubSubmitting={addClubSubmitting}
        onSubmit={handleAddClubSubmit}
        showOnboardPassword={showOnboardPassword}
        setShowOnboardPassword={setShowOnboardPassword}
        heroImageFile={heroImageFile}
        setHeroImageFile={setHeroImageFile}
        profileImageFile={profileImageFile}
        setProfileImageFile={setProfileImageFile}
        carouselImageFiles={carouselImageFiles}
        setCarouselImageFiles={setCarouselImageFiles}
        addClubHeroPasteUrl={addClubHeroPasteUrl}
        setAddClubHeroPasteUrl={setAddClubHeroPasteUrl}
        addClubProfilePasteUrl={addClubProfilePasteUrl}
        setAddClubProfilePasteUrl={setAddClubProfilePasteUrl}
        addClubCarouselPasteUrls={addClubCarouselPasteUrls}
        setAddClubCarouselPasteUrls={setAddClubCarouselPasteUrls}
        maxCarouselPasteUrls={onboardCarouselPasteCapacity}
        setImagePreviewUrls={setImagePreviewUrls}
        consolePaceUnit={consolePaceUnit}
      />
      <ClubsEditClubModal
        open={!!editModalClub}
        onRequestClose={tryCloseEditClubModal}
        editModalClub={editModalClub}
        editForm={editForm}
        setEditForm={setEditForm}
        editFormLoading={editFormLoading}
        editSubmitting={editSubmitting}
        onSubmit={handleEditClubSubmit}
        editHeroFile={editHeroFile}
        setEditHeroFile={setEditHeroFile}
        editProfileFile={editProfileFile}
        setEditProfileFile={setEditProfileFile}
        editHeroPasteUrl={editHeroPasteUrl}
        setEditHeroPasteUrl={setEditHeroPasteUrl}
        editProfilePasteUrl={editProfilePasteUrl}
        setEditProfilePasteUrl={setEditProfilePasteUrl}
        editCarouselSlots={editCarouselSlots}
        setEditCarouselSlots={setEditCarouselSlots}
        editCarouselBlobById={editCarouselBlobById}
        editFullClub={editFullClub}
        setEditFullClub={setEditFullClub}
        editScrapedImageRemovingUrl={editScrapedImageRemovingUrl}
        setEditScrapedImageRemovingUrl={setEditScrapedImageRemovingUrl}
        setImagePreviewUrls={setImagePreviewUrls}
        consolePaceUnit={consolePaceUnit}
        refetchClubs={refetchClubs}
        editPasteFieldSaving={editClubPasteFieldSaving}
        onSaveHeroPasteUrl={saveEditClubHeroPasteUrl}
        onSaveProfilePasteUrl={saveEditClubProfilePasteUrl}
        onSaveCarouselPasteUrl={saveEditClubCarouselPasteUrl}
      />
      <AlertDialog
        open={unsavedClubModalKind != null}
        onOpenChange={(next) => {
          if (!next) setUnsavedClubModalKind(null);
        }}
      >
        <AlertDialogContent className="console-page bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--console-ink)]">Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--console-muted)]">
              You have unsaved changes. If you leave now, they will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="tb-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction
              className="tb-btn bg-[var(--console-red)] hover:opacity-90"
              onClick={() => {
                if (unsavedClubModalKind === "add") closeAddClubModal();
                else if (unsavedClubModalKind === "edit") closeEditClubModal();
                setUnsavedClubModalKind(null);
              }}
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConsoleImagePreviewDialog urls={imagePreviewUrls} onClose={closeImagePreview} />

      <Dialog open={!!internalNotesModalClub} onOpenChange={(open) => !open && closeInternalNotesModal()}>
        <DialogContent className="max-w-4xl bg-[var(--console-surface)] border-[var(--console-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--console-ink)]">Internal notes</DialogTitle>
            {internalNotesModalClub && (
              <p className="text-sm text-[var(--console-muted)]">
                {internalNotesModalClub.name}
              </p>
            )}
          </DialogHeader>
          <div className="grid gap-2 py-4">
            <label htmlFor="internal-notes-textarea" className="text-sm font-medium text-[var(--console-ink)]">
              Admin-only notes (not shown to users)
            </label>
            <Textarea
              id="internal-notes-textarea"
              value={internalNotesValue}
              onChange={(e) => setInternalNotesValue(e.target.value)}
              placeholder="Add internal notes…"
              rows={4}
              maxLength={INTERNAL_NOTES_MAX_LENGTH}
              className="resize-none border-[var(--console-border)] bg-[var(--console-surface)]"
            />
          </div>
          <DialogFooter className="border-t border-[var(--console-border)] pt-4">
            <Button type="button" variant="outline" className="tb-btn" onClick={closeInternalNotesModal} disabled={internalNotesSaving}>
              Cancel
            </Button>
            <Button type="button" className="tb-btn primary" onClick={handleInternalNotesSave} disabled={internalNotesSaving}>
              {internalNotesSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!runsListModal} onOpenChange={(open) => !open && closeRunsListModal()}>
        <DialogContent className="console-dialog-content" style={{ maxWidth: 560 }}>
          <DialogHeader>
            <DialogTitle>
              {runsListModal?.mode === "completed"
                ? `Completed runs — ${getConsoleRangeLabel(meetingsRange)}`
                : `Upcoming runs — ${getConsoleRangeLabel(meetingsRange)}`}
              {runsListModal?.club && (
                <span className="block text-sm font-normal text-[var(--console-muted)] mt-1">
                  {runsListModal.club.name}
                </span>
              )}
              <span className="block text-xs font-normal text-[var(--console-muted)] mt-2 leading-snug">
                {formatConsoleScheduleWindowSubtitleUtc(meetingsRange)}
              </span>
            </DialogTitle>
          </DialogHeader>
          {runsListModal && (() => {
            const now = new Date();
            const clubRuns = runs.filter((r) => r.club_id === runsListModal.club.id);
            const list =
              runsListModal.mode === "upcoming"
                ? clubRuns
                    .filter((r) => isRunUpcomingInScheduleWindow(r, now, meetingsRange))
                    .sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""))
                : clubRuns
                    .filter((r) => isRunCompletedInScheduleWindow(r, now, meetingsRange))
                    .sort((a, b) => (b.scheduled_date || "").localeCompare(a.scheduled_date || ""));
            if (list.length === 0) {
              return (
                <p className="text-sm text-[var(--console-muted)] py-4">
                  {getConsoleScheduleWindowRunsEmptyMessage(
                    meetingsRange,
                    runsListModal.mode === "completed" ? "completed" : "upcoming"
                  )}
                </p>
              );
            }
            return (
              <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                {list.map((run) => {
                  const { date: dateStr, time: timeStr } = formatDateTimeInTimezone(
                    run.scheduled_date,
                    getConsoleRunDisplayTimezone(run)
                  );
                  const location = run.startLocation || run.address || run.start_location || "";
                  return (
                    <li
                      key={run.id}
                      role="button"
                      tabIndex={0}
                      className="border border-[var(--console-border)] rounded-lg p-3 bg-[var(--console-panel)] runs-table-row-clickable"
                      onClick={() => {
                        if (!run.id) return;
                        const cid = run.city_id || runsListModal.club?.city_id || cityId;
                        closeRunsListModal();
                        navigate(getConsoleRunUrl(cid, run.id, meetingsRange));
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        if (!run.id) return;
                        const cid = run.city_id || runsListModal.club?.city_id || cityId;
                        closeRunsListModal();
                        navigate(getConsoleRunUrl(cid, run.id, meetingsRange));
                        e.preventDefault();
                      }}
                    >
                      <div className="font-medium text-[var(--console-ink)]">
                        {run.name || "Unnamed run"}
                      </div>
                      <div className="text-sm text-[var(--console-muted)] mt-1">
                        {run.scheduled_date ? (
                          <>
                            {dateStr}
                            {timeStr && ` · ${timeStr}`}
                          </>
                        ) : (
                          "No date"
                        )}
                      </div>
                      {isConsoleRunTimezoneUnset(run) && (
                        <p className="text-xs text-[var(--console-muted)] mt-1 mb-0" role="note">
                          {CONSOLE_RUN_TIMEZONE_UNSET_NOTE_LIST}
                        </p>
                      )}
                      {location && (
                        <div className="text-sm text-[var(--console-ink)] mt-1" title={location}>
                          {location.length > 60 ? `${location.slice(0, 60)}…` : location}
                        </div>
                      )}
                      {run.what_to_expect && (
                        <div className="text-xs text-[var(--console-muted)] mt-2 line-clamp-2">
                          {run.what_to_expect}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {run.status && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              isMeetingVisible(run)
                                ? "bg-[var(--console-green)]/20 text-[var(--console-green)]"
                                : "bg-[var(--console-muted)]/20 text-[var(--console-muted)]"
                            }`}
                          >
                            {run.status}
                          </span>
                        )}
                        {run.event_type && (
                          <span className="text-xs text-[var(--console-muted)]">{run.event_type}</span>
                        )}
                        {run.difficulty && (
                          <span className="text-xs text-[var(--console-muted)]">{run.difficulty}</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={closeRunsListModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!membersModalClub} onOpenChange={(open) => !open && closeMembersModal()}>
        <DialogContent
          className="console-dialog-content border text-[var(--console-ink)]"
          style={{
            maxWidth: 560,
            background: "var(--console-surface, #ffffff)",
            borderColor: "var(--console-border, #e5e5e5)",
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-[var(--console-ink)]">
              Members
              {membersModalClub && (
                <span className="block text-sm font-normal text-[var(--console-muted)] mt-1">
                  {membersModalClub.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {membersLoading ? (
            <p className="text-sm text-[var(--console-muted)] py-4">Loading…</p>
          ) : membersError ? (
            <p className="text-sm text-[var(--console-red)] py-4">Failed to load members.</p>
          ) : membersList.length === 0 ? (
            <p className="text-sm text-[var(--console-muted)] py-4">No members.</p>
          ) : (
            <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 list-none">
              {membersList.map((m, idx) => (
                <li
                  key={m.user_id || m.id || `member-${idx}`}
                  className="border border-[var(--console-border)] rounded-lg p-3 bg-[var(--console-panel)] flex items-center gap-3"
                >
                  {m.profile_picture && (
                    <img
                      src={m.profile_picture}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-[var(--console-border)]"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--console-ink)]">{m.name || "Unknown"}</div>
                    {m.city && <div className="text-sm text-[var(--console-muted)] mt-0.5">{m.city}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!membersLoading && membersTotal > membersList.length && (
            <p className="text-xs text-[var(--console-muted)] mt-2">
              Showing {membersList.length} of {membersTotal} members
            </p>
          )}
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={closeMembersModal}
              className="border-[var(--console-border, #e5e5e5)]"
              style={{ borderWidth: "1px", borderStyle: "solid" }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!upcomingRunsModalClub} onOpenChange={(open) => !open && closeUpcomingRunsModal()}>
        <DialogContent className="console-dialog-content" style={{ maxWidth: 560 }}>
          <DialogHeader>
            <DialogTitle>
              {`Upcoming runs — ${getConsoleRangeLabel(meetingsRange)}`}
              {upcomingRunsModalClub && (
                <span className="block text-sm font-normal text-[var(--console-muted)] mt-1">
                  {upcomingRunsModalClub.name}
                </span>
              )}
              <span className="block text-xs font-normal text-[var(--console-muted)] mt-2 leading-snug">
                {formatConsoleScheduleWindowSubtitleUtc(meetingsRange)}
              </span>
              {meetingsRange === "next_7_days" &&
                recurringQualityUtcRange != null &&
                recurringQualityUtcRange !== "" && (
                  <span className="block text-xs font-normal text-[var(--console-muted)] mt-2 leading-snug">
                    {recurringQualityUtcRange} (same window as onboarding recurring count)
                  </span>
                )}
            </DialogTitle>
          </DialogHeader>
          {upcomingRunsLoading ? (
            <p className="text-sm text-[var(--console-muted)] py-4">Loading…</p>
          ) : upcomingRunsList.length === 0 ? (
            <p className="text-sm text-[var(--console-muted)] py-4">
              {getConsoleScheduleWindowRunsEmptyMessage(meetingsRange, "upcoming")}
            </p>
          ) : (
            <ul className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {upcomingRunsList.map((run) => {
                const { date: dateStr, time: timeStr } = formatDateTimeInTimezone(
                  run.scheduled_date,
                  getConsoleRunDisplayTimezone(run)
                );
                const location = run.startLocation || run.address || run.start_location || "";
                return (
                  <li
                    key={run.id}
                    role="button"
                    tabIndex={0}
                    className="border border-[var(--console-border)] rounded-lg p-3 bg-[var(--console-panel)] runs-table-row-clickable"
                    onClick={() => {
                      if (!run.id) return;
                      const cid = run.city_id || upcomingRunsModalClub?.city_id || cityId;
                      closeUpcomingRunsModal();
                      navigate(getConsoleRunUrl(cid, run.id, meetingsRange));
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      if (!run.id) return;
                      const cid = run.city_id || upcomingRunsModalClub?.city_id || cityId;
                      closeUpcomingRunsModal();
                      navigate(getConsoleRunUrl(cid, run.id, meetingsRange));
                      e.preventDefault();
                    }}
                  >
                    <div className="font-medium text-[var(--console-ink)]">
                      {run.name || "Unnamed run"}
                    </div>
                    <div className="text-sm text-[var(--console-muted)] mt-1">
                      {dateStr}
                      {timeStr && ` · ${timeStr}`}
                    </div>
                    {isConsoleRunTimezoneUnset(run) && (
                      <p className="text-xs text-[var(--console-muted)] mt-1 mb-0" role="note">
                        {CONSOLE_RUN_TIMEZONE_UNSET_NOTE_LIST}
                      </p>
                    )}
                    {location && (
                      <div className="text-sm text-[var(--console-ink)] mt-1" title={location}>
                        {location.length > 60 ? `${location.slice(0, 60)}…` : location}
                      </div>
                    )}
                    {run.what_to_expect && (
                      <div className="text-xs text-[var(--console-muted)] mt-2 line-clamp-2">
                        {run.what_to_expect}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {run.status && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            isMeetingVisible(run)
                              ? "bg-[var(--console-green)]/20 text-[var(--console-green)]"
                              : "bg-[var(--console-muted)]/20 text-[var(--console-muted)]"
                          }`}
                        >
                          {run.status}
                        </span>
                      )}
                      {run.event_type && (
                        <span className="text-xs text-[var(--console-muted)]">{run.event_type}</span>
                      )}
                      {run.difficulty && (
                        <span className="text-xs text-[var(--console-muted)]">{run.difficulty}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={closeUpcomingRunsModal}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConsoleViewShell>
  );
}
